// Issue-related filters

import { IssueStatus, IssueType, Priority } from '@supply-mover-2-0/shared';

import {
	createDateRangeFilter,
	createEntityLookupFilter,
	createKeywordFilter,
} from './factories';
import type { FilterRegistry } from './types';

export const issueFilters: FilterRegistry = {
	issueKeywords: {
		...createKeywordFilter({
			label: 'Search',
			placeholder: 'Search issues...',
			objectTable: 'issue',
			listKeys: [
				'issue',
				'issue-client-embedded',
				'issue-vendor-embedded',
				'issue-sourcing-request-embedded',
			],
			fulltextFields: ['name', 'description'],
		}),
		queryParamName: 'issueKeywords',
	},

	issueStatus: {
		type: 'multi-select',
		label: 'Status',
		placeholder: 'Select status...',
		colorScheme: 'blue',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'issueStatus',
		multiple: true,
		valueExtractor: {
			property: 'id',
		},
		options: Object.values(IssueStatus).map((status) => ({
			id: status,
			label: status,
		})),
		queryMapping: {
			objectTable: 'issue',
			fieldName: 'status',
			joinType: 'direct',
		},
		format: 'array',
		listKeys: [
			'issue',
			'issue-client-embedded',
			'issue-vendor-embedded',
			'issue-sourcing-request-embedded',
		],
	},

	issueType: {
		type: 'multi-select',
		label: 'Type',
		placeholder: 'Select type...',
		colorScheme: 'purple',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'issueType',
		multiple: true,
		valueExtractor: {
			property: 'id',
		},
		options: Object.values(IssueType).map((type) => ({
			id: type,
			label: type,
		})),
		queryMapping: {
			objectTable: 'issue',
			fieldName: 'type',
			joinType: 'direct',
		},
		format: 'array',
		listKeys: [
			'issue',
			'issue-client-embedded',
			'issue-vendor-embedded',
			'issue-sourcing-request-embedded',
		],
	},

	issuePriority: {
		type: 'multi-select',
		label: 'Priority',
		placeholder: 'Select priority...',
		colorScheme: 'orange',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'issuePriority',
		multiple: true,
		valueExtractor: {
			property: 'id',
		},
		options: Object.values(Priority).map((priority) => ({
			id: priority,
			label: priority.charAt(0).toUpperCase() + priority.slice(1),
		})),
		queryMapping: {
			objectTable: 'issue',
			fieldName: 'priority',
			joinType: 'direct',
		},
		format: 'array',
		listKeys: ['issue'],
	},

	issueClient: {
		...createEntityLookupFilter({
			label: 'Client',
			placeholder: 'Search clients...',
			apiEndpoint: '/clients',
			objectTable: 'issue',
			fieldName: 'clientId',
			listKeys: ['issue'],
			displayFields: {
				id: 'id',
				primary: 'name',
				secondary: 'externalId',
			},
			colorScheme: 'green',
			joinType: 'exists',
		}),
		searchFilterName: 'clientSearch',
		apiParams: {
			page: '1',
			limit: '10',
			fields: JSON.stringify(['client.id', 'client.name', 'client.externalId']),
		},
		queryParamName: 'issueClient',
		valueExtractor: {
			property: 'id',
			transform: 'number',
		},
	},

	issueAssignee: {
		type: 'multi-select',
		label: 'Assigned To',
		placeholder: 'Select users...',
		colorScheme: 'blue',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'display_name' as keyof unknown,
		},
		queryParamName: 'issueAssignee',
		apiEndpoint: '/users',
		multiple: true,
		options: [
			{ id: 'ME', label: 'Me' },
			{ id: 'null', label: 'Unassigned' },
		],
		queryMapping: {
			objectTable: 'issue',
			fieldName: '_fk_user__id_assignee',
			joinType: 'direct',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['issue'],
	},

	issueCreatedDateRange: {
		...createDateRangeFilter({
			label: 'Created Date',
			objectTable: 'issue',
			dateField: '_created_date',
			listKeys: ['issue'],
		}),
		queryParamName: 'issueCreatedDateRange',
		allowClear: true,
	},

	issueClientSalesRep: {
		type: 'multi-select',
		label: 'Client Sales Rep',
		placeholder: 'Select sales rep...',
		colorScheme: 'blue',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'salesperson_id' as keyof unknown,
			primary: 'display_name' as keyof unknown,
		},
		apiEndpoint: '/users/salespeople',
		multiple: true,
		options: [{ id: 'ME', label: 'Me' }],
		queryMapping: {
			objectTable: 'issue',
			fieldName: 'clientSalesRepId',
			joinType: 'custom',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['issue'],
		queryParamName: 'issueClientSalesRep',
	},

	issueNotesModifiedDateRange: {
		...createDateRangeFilter({
			label: 'Modified Date',
			objectTable: 'issue_note',
			dateField: '_modified_date',
			listKeys: ['issue_notes'],
		}),
		queryParamName: 'issueNotesModifiedDateRange',
	},

	modifiedByUser: {
		type: 'multi-select',
		label: 'Modified By User',
		placeholder: 'Search users...',
		colorScheme: 'green',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'workdUserId' as keyof unknown,
			primary: 'display_name' as keyof unknown,
		},
		apiEndpoint: '/users',
		queryParamName: 'modifiedByUser',
		multiple: true,
		queryMapping: {
			objectTable: 'issue_note',
			fieldName: '_modified_userId',
			joinType: 'direct',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
			transform: 'number',
		},
		listKeys: ['issue_notes'],
	},

	issueLinkEntityType: {
		type: 'multi-select',
		label: 'Link Type',
		placeholder: 'Select link type...',
		colorScheme: 'purple',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'issueLinkEntityType',
		multiple: true,
		options: [
			{ id: 'client', label: 'Client' },
			{ id: 'order', label: 'Order' },
			{ id: 'invoice', label: 'Invoice' },
			{ id: 'quote', label: 'Quote' },
			{ id: 'item', label: 'Item' },
			{ id: 'vendor', label: 'Vendor' },
			{ id: 'sourcing_request', label: 'Sourcing Request' },
		],
		queryMapping: {
			objectTable: 'issue_link',
			fieldName: 'entityType',
			joinType: 'direct',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['issue_links'],
	},
};
